if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.galleryDotsRaf = null;
        if (!this.elements.viewer) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.viewer.addEventListener('slideChanged', this.syncGalleryDots.bind(this));

        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
          this.initThumbnailArrowControls();
        }
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        this.initProductGalleryDots();
      }

      initProductGalleryDots() {
        this.galleryDotsRoot = this.querySelector('[data-gallery-dots]');
        if (!this.galleryDotsRoot || !this.elements.viewer) return;
        this.viewerMainSlider = this.elements.viewer.slider;
        this.galleryDotButtons = this.galleryDotsRoot.querySelectorAll('.product__media-gallery-dot');
        if (!this.viewerMainSlider || this.galleryDotButtons.length === 0) return;

        this.onViewerScrollForDots = () => {
          if (this.galleryDotsRaf) return;
          this.galleryDotsRaf = window.requestAnimationFrame(() => {
            this.galleryDotsRaf = null;
            this.syncGalleryDots();
          });
        };

        this.viewerMainSlider.addEventListener('scroll', this.onViewerScrollForDots, { passive: true });
        this.galleryDotButtons.forEach((btn) => {
          btn.addEventListener('click', this.onGalleryDotClick.bind(this));
        });
        this.syncGalleryDots();
      }

      initThumbnailArrowControls() {
        this.thumbnailSlider = this.elements.thumbnails?.slider;
        if (!this.thumbnailSlider) return;

        this.thumbnailPrevButton = this.elements.thumbnails.querySelector('button[name="previous"]');
        this.thumbnailNextButton = this.elements.thumbnails.querySelector('button[name="next"]');
        if (!this.thumbnailPrevButton || !this.thumbnailNextButton) return;

        // Reset stale disabled state before real metrics settle.
        this.thumbnailPrevButton.removeAttribute('disabled');
        this.thumbnailNextButton.removeAttribute('disabled');

        this.onThumbnailArrowClick = (direction, event) => {
          if (!this.isVerticalThumbnailDesktop()) return;

          event.preventDefault();
          event.stopImmediatePropagation();

          const items = this.getVisibleThumbnailItems();
          if (items.length <= 5) return;

          const step = Math.max(1, Number(event.currentTarget.dataset.step || 1));
          const epsilon = 1;
          const scrollTop = this.thumbnailSlider.scrollTop;
          const viewportBottom = scrollTop + this.thumbnailSlider.clientHeight;

          let anchorIndex = items.findIndex((item) => item.offsetTop + item.clientHeight > scrollTop + epsilon);
          if (anchorIndex === -1) anchorIndex = 0;

          if (direction === 'next') {
            const nextIndex = items.findIndex((item) => item.offsetTop + item.clientHeight > viewportBottom + epsilon);
            if (nextIndex === -1) return;
            anchorIndex = Math.min(items.length - 1, nextIndex + step - 1);
          } else {
            const previousItems = items.filter((item) => item.offsetTop < scrollTop - epsilon);
            if (!previousItems.length) return;
            anchorIndex = Math.max(0, previousItems.length - step);
          }

          const maxTop = Math.max(0, this.thumbnailSlider.scrollHeight - this.thumbnailSlider.clientHeight);
          const nextTop = Math.min(maxTop, Math.max(0, items[anchorIndex].offsetTop));
          this.thumbnailSlider.scrollTo({ top: nextTop, behavior: 'smooth' });

          window.requestAnimationFrame(() => this.updateThumbnailArrowState());
        };

        this.thumbnailPrevButton.addEventListener('click', this.onThumbnailArrowClick.bind(this, 'prev'), true);
        this.thumbnailNextButton.addEventListener('click', this.onThumbnailArrowClick.bind(this, 'next'), true);

        this.thumbnailSlider.addEventListener(
          'scroll',
          () => {
            if (!this.isVerticalThumbnailDesktop()) return;
            this.updateThumbnailArrowState();
          },
          { passive: true }
        );

        this.thumbnailSlider
          .querySelectorAll('.thumbnail-list__item img')
          .forEach((img) => img.addEventListener('load', () => this.updateThumbnailArrowState(), { once: true }));

        window.addEventListener('resize', () => {
          this.updateThumbnailArrowState();
        });

        this.updateThumbnailArrowState();
        window.requestAnimationFrame(() => this.updateThumbnailArrowState());
      }

      isVerticalThumbnailDesktop() {
        return (
          this.thumbnailSlider?.classList?.contains('slider--vertical-desktop') &&
          window.matchMedia('(min-width: 990px)').matches
        );
      }

      getThumbnailStepOffset() {
        const items = Array.from(this.thumbnailSlider.querySelectorAll('.thumbnail-list__item.slider__slide')).filter(
          (item) => item.clientHeight > 0
        );
        if (items.length < 2) {
          return items[0]?.clientHeight || 0;
        }

        let step = items[1].offsetTop - items[0].offsetTop;
        if (step <= 0) {
          const gap = Number.parseFloat(window.getComputedStyle(this.thumbnailSlider).rowGap || '0') || 0;
          step = items[0].getBoundingClientRect().height + gap;
        }
        return step;
      }

      getVisibleThumbnailItems() {
        return Array.from(this.thumbnailSlider.querySelectorAll('.thumbnail-list__item.slider__slide')).filter(
          (item) => item.clientHeight > 0
        );
      }

      updateThumbnailArrowState() {
        if (!this.isVerticalThumbnailDesktop() || !this.thumbnailPrevButton || !this.thumbnailNextButton) return;

        this.thumbnailPrevButton.removeAttribute('disabled');
        this.thumbnailNextButton.removeAttribute('disabled');

        const items = this.getVisibleThumbnailItems();
        const epsilon = 1;
        const scrollTop = this.thumbnailSlider.scrollTop;
        const viewportBottom = scrollTop + this.thumbnailSlider.clientHeight;
        const firstItem = items[0];
        const lastItem = items[items.length - 1];
        const canScroll = items.length > 5;
        const prevInactive = !firstItem || !canScroll || firstItem.offsetTop >= scrollTop - epsilon;
        const nextInactive =
          !lastItem || !canScroll || lastItem.offsetTop + lastItem.clientHeight <= viewportBottom + epsilon;

        this.thumbnailPrevButton.hidden = !canScroll;
        this.thumbnailNextButton.hidden = !canScroll;

        this.thumbnailPrevButton.classList.toggle('is-inactive', prevInactive);
        this.thumbnailPrevButton.setAttribute('aria-disabled', prevInactive ? 'true' : 'false');
        this.thumbnailNextButton.classList.toggle('is-inactive', nextInactive);
        this.thumbnailNextButton.setAttribute('aria-disabled', nextInactive ? 'true' : 'false');
      }

      onGalleryDotClick(event) {
        const btn = event.currentTarget;
        const targetFromDot = parseInt(btn.dataset.galleryDotTarget, 10);
        const index = Number.isNaN(targetFromDot) ? parseInt(btn.dataset.galleryDotIndex, 10) : targetFromDot;
        if (Number.isNaN(index)) return;
        this.goToGallerySlide(index);
      }

      goToGallerySlide(index) {
        if (!this.viewerMainSlider) return;
        const slides = this.viewerMainSlider.querySelectorAll('[data-media-id]');
        const slide = slides[index];
        if (!slide) return;
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.viewerMainSlider.scrollTo({
          left: slide.offsetLeft,
          behavior: reduceMotion ? 'auto' : 'smooth',
        });
        window.requestAnimationFrame(() => this.syncGalleryDots());
      }

      syncGalleryDots() {
        if (!this.viewerMainSlider || !this.galleryDotButtons || this.galleryDotButtons.length === 0) return;
        const slides = this.viewerMainSlider.querySelectorAll('[data-media-id]');
        if (!slides.length) return;

        const center = this.viewerMainSlider.scrollLeft + this.viewerMainSlider.clientWidth / 2;
        let bestIndex = 0;
        let bestDist = Infinity;
        slides.forEach((slide, i) => {
          const slideCenter = slide.offsetLeft + slide.clientWidth / 2;
          const dist = Math.abs(slideCenter - center);
          if (dist < bestDist) {
            bestDist = dist;
            bestIndex = i;
          }
        });

        let closestDotIndex = 0;
        let closestDotDistance = Infinity;
        this.galleryDotButtons.forEach((btn, i) => {
          const dotTarget = parseInt(btn.dataset.galleryDotTarget, 10);
          const mappedTarget = Number.isNaN(dotTarget) ? i : dotTarget;
          const targetDistance = Math.abs(mappedTarget - bestIndex);
          if (targetDistance < closestDotDistance) {
            closestDotDistance = targetDistance;
            closestDotIndex = i;
          }
        });

        this.galleryDotButtons.forEach((btn, i) => {
          btn.classList.toggle('slider-counter__link--active', i === closestDotIndex);
        });
      }

      onSlideChanged(event) {
        this.setCurrentMediaState(event.detail.currentElement);
        this.syncGalleryDots();

        if (!this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setCurrentMediaState(activeMedia) {
        if (!activeMedia || !this.elements.viewer) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.toggle('is-active', element === activeMedia);
        });
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.setCurrentMediaState(activeMedia);

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft,
              behavior: reduceMotion ? 'auto' : 'smooth',
            });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(activeThumbnail);
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
        this.syncGalleryDots();
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        const thumbSlider = this.elements.thumbnails.slider;
        const verticalDesktop =
          thumbSlider.classList.contains('slider--vertical-desktop') &&
          window.matchMedia('(min-width: 990px)').matches;
        if (verticalDesktop) {
          thumbSlider.scrollTo({ top: Math.max(0, thumbnail.offsetTop) });
        } else {
          thumbSlider.scrollTo({ left: thumbnail.offsetLeft });
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image || !this.elements.liveRegion) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
